'use client';

import { useTranslations } from 'next-intl';
import { translateStageName } from '@/draw/text';
import { displayMatchCode } from '@/app/[locale]/event/[eventId]/libs/matchCode';
import type { MatchCode, PlaceBand, RoundAbbrev, SlotSource } from './ladderModel';

export function useLadderText() {
  const t = useTranslations('Event.drawView.ladder');
  const tPreview = useTranslations('AdminDraw.drawForm.drawPreview');

  const round = (a: RoundAbbrev): string => {
    switch (a.kind) {
      case 'final': return t('roundFinal');
      case 'sf': return t('roundSf');
      case 'qf': return t('roundQf');
      case 'roundOf': return t('roundOf', { players: a.players });
      case 'round': return t('roundNumber', { number: a.number });
    }
  };
  const roundLong = (a: RoundAbbrev): string => {
    switch (a.kind) {
      case 'final': return t('roundFinal');
      case 'sf': return t('roundSemiFinals');
      case 'qf': return t('roundQuarterFinals');
      case 'roundOf': return t('roundOfLong', { players: a.players });
      case 'round': return t('roundNumberLong', { number: a.number });
    }
  };
  const places = (b: PlaceBand) => t('places', { from: b.from, to: b.to });
  /** "3rd–4th · T1": names the playoff a code sits in, where nothing around it does. */
  const inPlayoff = (b: PlaceBand, c: string) => t('codeInPlayoff', { places: places(b), code: c });
  /** The stored match ID (A1); the round-derived code (QF1) only when a match has none. */
  const code = (c: MatchCode): string => {
    const stored = displayMatchCode(c.id);
    if (stored) return stored;
    const r = round(c.round);
    const base = c.index === null ? r : c.round.kind === 'roundOf' ? `${r}-${c.index}` : t('code', { round: r, index: c.index });
    return c.places ? inPlayoff(c.places, base) : base;
  };
  const stageName = (name: string) => translateStageName(name, tPreview);
  const source = (s: SlotSource): string => {
    if (s.kind === 'pool') return t('poolSeat', { pool: s.pool, rank: s.rank });
    const label = t(s.outcome === 'winner' ? 'winnerOf' : 'loserOf', { code: code(s.code) });
    return s.otherStageName ? t('fromStage', { label, stage: stageName(s.otherStageName) }) : label;
  };
  return { round, roundLong, code, places, inPlayoff, source, stageName };
}
